/**
 * Browser-to-browser voice on the perfect-negotiation pattern.
 *
 * Written fresh rather than ported from Craft Together's `useWebRTC.ts`, which
 * handles glare by decreeing that "player A always offers" — a rule that needs
 * an A and a B, which this project does not have, and
 * which fails anyway the moment either side needs to renegotiate.
 *
 * Perfect negotiation instead assigns one side the *polite* role and lets both
 * offer whenever they like. Politeness is derived from the two player ids by
 * string comparison, so both peers reach the same answer without being told,
 * and a third seat needs no new rule.
 *
 * Two ideas do survive from `useWebRTC.ts`, both hard-won:
 *   - buffer remote ICE candidates until a remote description exists
 *   - cap ICE restarts, so a genuinely broken path stops thrashing
 *
 * The media path is P2P; Cloudflare only relays when the network forces it,
 * and cannot decrypt what it relays.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { IceConfig, PlayerId, RtcSignal } from '@overcooked/shared'
import { SessionGate, newSessionId } from './peerSession'

export const MAX_ICE_RESTARTS = 2

/**
 * The restart budget, as an object rather than as a ref inside the effect.
 *
 * It lived in a ref that the negotiation effect reset on every run, and the
 * effect re-ran whenever `ice` changed — which is precisely what an ICE restart
 * asks for: `requestCredentials()`, a fresh `voiceCredentials` from the server,
 * a new `IceConfig` object into the deps. So every failure tore down the very
 * `RTCPeerConnection` `restartIce()` had just been called on, rebuilt it, sent a
 * `bye`, and set the count back to zero. On a genuinely dead TURN path that
 * loops forever and `MAX_ICE_RESTARTS` can never trip — the `status: 'failed'`
 * message below was unreachable code.
 *
 * The budget is per *call* now: it is created with the peer connection and
 * cleared when the path comes up, and a credential refresh is applied to the
 * live connection (see `applyIceConfig`) rather than being a reason to start
 * again.
 */
export interface IceRestarts {
  /** Spend one restart, or say there are none left. */
  onFailure(): 'restart' | 'give-up'
  /** A path that came up owes nothing: the next failure is a first failure. */
  onConnected(): void
  readonly count: number
}

export function createIceRestarts(max: number = MAX_ICE_RESTARTS): IceRestarts {
  let spent = 0
  return {
    onFailure() {
      if (spent >= max) return 'give-up'
      spent += 1
      return 'restart'
    },
    onConnected() {
      spent = 0
    },
    get count() {
      return spent
    },
  }
}

/**
 * Fresh TURN credentials, applied to the connection that needed them.
 *
 * `setConfiguration` is the whole reason a mint can arrive mid-call without
 * anything being torn down. Guarded because jsdom-free tests and very old
 * browsers may not have it; a browser that cannot take new servers keeps the
 * ones it has, which is no worse than before the refresh.
 */
export function applyIceConfig(
  pc: Pick<RTCPeerConnection, 'setConfiguration'> | null,
  ice: IceConfig | null,
): void {
  if (!pc || !ice || typeof pc.setConfiguration !== 'function') return
  pc.setConfiguration({ iceServers: ice.iceServers as RTCIceServer[] })
}

/**
 * What to do about `iceConnectionState === 'failed'`, with the budget in hand.
 *
 * Pulled out of the hook so the one thing that has to be true of it can be
 * asserted without a browser: a path that keeps failing gives up, and giving up
 * is reachable.
 */
export function onIceFailed(
  pc: Pick<RTCPeerConnection, 'restartIce'>,
  restarts: IceRestarts,
  deps: { requestCredentials(): void; patch(next: Partial<PeerVoiceState>): void },
): 'restart' | 'give-up' {
  if (restarts.onFailure() === 'give-up') {
    deps.patch({
      status: 'failed',
      error: "Voice chat couldn't connect. You can still play. Try reloading the page.",
    })
    return 'give-up'
  }
  deps.patch({ status: 'reconnecting' })
  // Fresh credentials first: an hour-old TURN password is a plausible reason for
  // the failure we are restarting because of. They arrive as a new `IceConfig`
  // and are applied to *this* connection rather than replacing it.
  deps.requestCredentials()
  pc.restartIce()
  return 'restart'
}

export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'

export interface PeerVoiceOptions {
  /** The microphone, already acquired by the mic check. Null until it is. */
  localStream: MediaStream | null
  selfId: PlayerId | null
  peerId: PlayerId | null
  ice: IceConfig | null
  sendSignal(to: PlayerId, signal: RtcSignal): void
  onSignal(handler: (from: PlayerId, signal: RtcSignal) => void): () => void
  /** Ask the server for a fresh mint before an ICE restart. */
  requestCredentials(): void
  /** How often to sample `getStats()` for the round-trip time. */
  sampleIntervalMs: number
  onRttSample(rttMs: number): void
}

export interface PeerVoiceState {
  status: VoiceStatus
  /** Most recent voice-path round trip, for the on-screen readout. */
  rttMs: number | null
  /** True once the selected candidate pair is a TURN relay rather than P2P. */
  relayed: boolean
  error: string | null
}

/**
 * Read the round-trip time off the selected candidate pair.
 *
 * `currentRoundTripTime` is in seconds and is only present once STUN has
 * completed a consent check on the pair, so it is legitimately undefined for
 * the first second or two of a call.
 */
export async function readVoiceStats(
  pc: RTCPeerConnection,
): Promise<{ rttMs: number | null; relayed: boolean }> {
  let rttMs: number | null = null
  let relayed = false

  const report = await pc.getStats()
  const candidates = new Map<string, { candidateType?: string }>()
  report.forEach((stat) => {
    if (stat.type === 'local-candidate' || stat.type === 'remote-candidate') {
      candidates.set(stat.id, stat as { candidateType?: string })
    }
  })

  report.forEach((stat) => {
    if (stat.type !== 'candidate-pair') return
    const pair = stat as RTCIceCandidatePairStats & { selected?: boolean }
    // Chrome marks the pair `selected`; Firefox reports `state: 'succeeded'`
    // and `nominated`. Accepting either avoids a browser-shaped blind spot.
    const chosen = pair.selected === true || (pair.state === 'succeeded' && pair.nominated === true)
    if (!chosen) return
    if (typeof pair.currentRoundTripTime === 'number') rttMs = pair.currentRoundTripTime * 1000
    const local = pair.localCandidateId ? candidates.get(pair.localCandidateId) : undefined
    if (local?.candidateType === 'relay') relayed = true
  })

  return { rttMs, relayed }
}

/**
 * The polite peer yields when its own offer collides with an incoming one.
 * Comparing ids gives both sides the same answer with no coordination.
 */
export function isPolite(selfId: PlayerId, peerId: PlayerId): boolean {
  return selfId < peerId
}

export function usePeerVoice(options: PeerVoiceOptions): PeerVoiceState {
  const {
    localStream,
    selfId,
    peerId,
    ice,
    sendSignal,
    onSignal,
    requestCredentials,
    sampleIntervalMs,
    onRttSample,
  } = options

  const [state, setState] = useState<PeerVoiceState>({
    status: 'idle',
    rttMs: null,
    relayed: false,
    error: null,
  })

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const makingOfferRef = useRef(false)
  const ignoreOfferRef = useRef(false)
  const candidateBufferRef = useRef<RTCIceCandidateInit[]>([])
  const restartsRef = useRef<IceRestarts>(createIceRestarts())
  /**
   * Which connection is which, at both ends — see `voice/peerSession.ts`. It
   * outlives the effect so a rebuild inherits the list of the other side's
   * connections already left behind. `gatePeerRef` resets it for a new partner.
   */
  const gateRef = useRef<SessionGate | null>(null)
  const gatePeerRef = useRef<PlayerId | null>(null)
  /** The offer that asked for a rebuild, and anything behind it, for the new connection. */
  const pendingRef = useRef<Array<{ from: PlayerId; signal: RtcSignal }>>([])
  /** Bumped when the other side has started a new connection and this one must follow. */
  const [generation, setGeneration] = useState(0)
  // The latest mint, read rather than depended on. A credential refresh is a
  // fact about the servers this connection should try, not a reason to build a
  // new connection — see `IceRestarts` above.
  const iceRef = useRef(ice)
  iceRef.current = ice

  // Latest-value refs so the negotiation effect does not tear down and rebuild
  // the peer connection every time a callback identity changes.
  const sendRef = useRef(sendSignal)
  sendRef.current = sendSignal
  const onSignalRef = useRef(onSignal)
  onSignalRef.current = onSignal
  const requestRef = useRef(requestCredentials)
  requestRef.current = requestCredentials
  const sampleRef = useRef(onRttSample)
  sampleRef.current = onRttSample

  const patch = useCallback((next: Partial<PeerVoiceState>) => {
    setState((previous) => ({ ...previous, ...next }))
  }, [])

  /** There is a mint to build a connection with. Which mint is `iceRef`'s. */
  const iceReady = ice !== null

  // Fresh credentials, onto the live connection. Separate from the negotiation
  // effect on purpose: this is the half that used to be a teardown.
  useEffect(() => {
    applyIceConfig(pcRef.current, ice)
  }, [ice])

  useEffect(() => {
    if (!localStream || !selfId || !peerId || !iceRef.current) return

    const polite = isPolite(selfId, peerId)
    const gate =
      gateRef.current && gatePeerRef.current === peerId
        ? gateRef.current.successor(newSessionId())
        : new SessionGate(newSessionId())
    gateRef.current = gate
    gatePeerRef.current = peerId
    // Every signal leaves naming this connection and the one it is meant for.
    const send = (signal: RtcSignal): void => sendRef.current(peerId, gate.stamp(signal))
    let rebuilding = false
    const pc = new RTCPeerConnection({ iceServers: iceRef.current.iceServers as RTCIceServer[] })
    pcRef.current = pc
    restartsRef.current = createIceRestarts()
    candidateBufferRef.current = []
    patch({ status: 'connecting', error: null })

    for (const track of localStream.getAudioTracks()) pc.addTrack(track, localStream)

    const audio = new Audio()
    audio.autoplay = true
    audioRef.current = audio

    pc.ontrack = ({ streams }) => {
      audio.srcObject = streams[0] ?? null
      // Autoplay may be blocked until a gesture; the mic-check click counts,
      // but a rejection here must not take the connection down with it.
      void audio.play().catch(() => undefined)
    }

    pc.onicecandidate = ({ candidate }) => {
      send({ kind: 'candidate', candidate: candidate ? candidate.toJSON() : null })
    }

    pc.onnegotiationneeded = () => {
      void (async () => {
        try {
          makingOfferRef.current = true
          await pc.setLocalDescription()
          if (!pc.localDescription) return
          send({
            kind: 'description',
            description: {
              type: pc.localDescription.type as 'offer' | 'answer',
              sdp: pc.localDescription.sdp,
            },
          })
        } catch (err) {
          console.warn('[voice] negotiation failed:', err)
        } finally {
          makingOfferRef.current = false
        }
      })()
    }

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        // The path came up, so the retries it cost are spent and forgiven.
        restartsRef.current.onConnected()
        patch({ status: 'connected', error: null })
      }
      if (pc.connectionState === 'disconnected') patch({ status: 'reconnecting' })
    }

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState !== 'failed') return
      onIceFailed(pc, restartsRef.current, {
        requestCredentials: () => requestRef.current(),
        patch,
      })
    }

    const addCandidate = async (init: RTCIceCandidateInit | null): Promise<void> => {
      if (!init) return
      if (!pc.remoteDescription) {
        candidateBufferRef.current.push(init)
        return
      }
      await pc.addIceCandidate(init).catch((err) => console.warn('[voice] addIceCandidate:', err))
    }

    const flushCandidates = async (): Promise<void> => {
      const buffered = candidateBufferRef.current
      candidateBufferRef.current = []
      for (const init of buffered) {
        await pc.addIceCandidate(init).catch((err) => console.warn('[voice] buffered candidate:', err))
      }
    }

    const handle = (from: PlayerId, signal: RtcSignal): void => {
      if (from !== peerId) return
      // Once this connection has been told to make way, everything after the
      // offer that said so belongs to its replacement.
      if (rebuilding) {
        pendingRef.current.push({ from, signal })
        return
      }
      const admission = gate.admit(signal)
      if (admission === 'drop') return
      if (admission === 'rebuild') {
        rebuilding = true
        pendingRef.current.push({ from, signal })
        setGeneration((n) => n + 1)
        return
      }
      void (async () => {
        try {
          if (signal.kind === 'candidate') {
            await addCandidate(signal.candidate as RTCIceCandidateInit | null)
            return
          }
          if (signal.kind === 'bye') return

          const description = signal.description
          // The collision test, verbatim from the perfect-negotiation pattern.
          const offerCollision =
            description.type === 'offer' && (makingOfferRef.current || pc.signalingState !== 'stable')
          ignoreOfferRef.current = !polite && offerCollision
          if (ignoreOfferRef.current) return

          await pc.setRemoteDescription(description as RTCSessionDescriptionInit)
          await flushCandidates()

          if (description.type === 'offer') {
            await pc.setLocalDescription()
            if (!pc.localDescription) return
            send({
              kind: 'description',
              description: {
                type: pc.localDescription.type as 'offer' | 'answer',
                sdp: pc.localDescription.sdp,
              },
            })
          }
        } catch (err) {
          console.warn('[voice] signal handling failed:', err)
        }
      })()
    }

    const unsubscribe = onSignalRef.current(handle)
    // What a predecessor handed over: the offer that asked for this connection.
    const handedOver = pendingRef.current
    pendingRef.current = []
    for (const { from, signal } of handedOver) handle(from, signal)

    /**
     * The voice path's latency, sampled on the game clock. This is the number
     * that answers "is voice fast enough?" with evidence rather than opinion,
     * so it goes into the log rather than only onto the screen.
     */
    const statsTimer = setInterval(() => {
      void readVoiceStats(pc).then(({ rttMs, relayed }) => {
        if (rttMs === null) return
        patch({ rttMs: Math.round(rttMs), relayed })
        sampleRef.current(rttMs)
      })
    }, sampleIntervalMs)

    return () => {
      clearInterval(statsTimer)
      unsubscribe()
      send({ kind: 'bye' })
      pc.ontrack = null
      pc.onicecandidate = null
      pc.onnegotiationneeded = null
      pc.onconnectionstatechange = null
      pc.oniceconnectionstatechange = null
      pc.close()
      pcRef.current = null
      audio.srcObject = null
      audioRef.current = null
      patch({ status: 'idle', rttMs: null, relayed: false })
    }
    // `sendSignal`/`onSignal` are read through refs above; re-running on their
    // identity would tear down a working call on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `ice` is deliberately absent: a fresh mint is applied to this connection
    // by the effect above. Depending on it here is what made `MAX_ICE_RESTARTS`
    // unreachable. `iceReady` is in, because the *first* mint is what there is
    // to wait for.
    // `generation` is in: an offer from a connection the other side has only
    // just started (a reload, or a rebuild over there) cannot be applied to
    // this one, so this one is replaced and the offer handed to its successor.
  }, [localStream, selfId, peerId, iceReady, sampleIntervalMs, patch, generation])

  return state
}
